import { ReactElement } from 'react';
import { Form, SelectField, Submit } from '@ff-it/form';
import { Alert, Box, DialogBody, DialogFooter, DialogHeader, LoadingContainer, RenderProps, Stack } from '@ff-it/ui';
import useSWR from 'swr';
import invariant from 'tiny-invariant';
import { type BlockState, blockStates } from 'modules/campaign/block/types';
import type { BlockStatus, BlockStatusTemplate } from 'modules/campaign/status/types';
import { BlockStateHelpLabel } from 'modules/campaign/status/BlockStateHelpLabel';
import { required } from 'utilities';
import { BlockStatusTag } from 'modules/campaign/status/BlockStatusTag';

type RemapStateGroup = {
  state: BlockState;
  statuses: { index: number; current: BlockStatus; options: BlockStatus[] }[];
};
type RemapProps = {
  current: BlockStatusTemplate;
  next: BlockStatusTemplate;
  states: RemapStateGroup[];
};

function RemapForm({ current, next, states }: RemapProps): ReactElement {
  return (
    <Stack direction="vertical">
      <Stack direction="horizontal" fontWeight="semibold">
        <Box width="full" display="flex" alignItems="center">
          {current.name}
        </Box>
        <Box width="full" display="flex" alignItems="center">
          {next.name}
        </Box>
      </Stack>
      {states.map(({ state, statuses }) => (
        <Box mb="1" key={state}>
          <Box display="flex" alignItems="center" mb="1">
            <BlockStateHelpLabel>{state}</BlockStateHelpLabel>
          </Box>
          <Box>
            {statuses.map(({ index, current, options }) => {
              return (
                <Stack key={current.id} direction="horizontal" mb="1">
                  <Box width="full" display="flex" alignItems="center">
                    <BlockStatusTag appearance="naked" status={current} py="2" />
                  </Box>
                  <Box width="full">
                    {/* select next for state */}
                    <SelectField
                      name={`statuses[${index}].next`}
                      options={options}
                      formatOptionLabel={(status) => <BlockStatusTag appearance="naked" status={status} />}
                      getOptionValue={(v) => v.id.toString()}
                      className="mb-0"
                      required
                      validate={required}
                    />
                  </Box>
                </Stack>
              );
            })}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

type RemapCandidate = { current: BlockStatus; next: BlockStatus | null };

type InitialState = {
  statuses: RemapCandidate[];
};

const useProps = (
  currentId: number,
  nextId: number,
): { initialValues: InitialState; remap: RemapProps } | undefined => {
  const { data } = useSWR<BlockStatusTemplate[], unknown>({
    url: 'campaign/statuses/',
    method: 'GET',
  });

  if (!data) {
    return undefined;
  }
  // 0 is same as default but we don't know what iis default
  const current = data.find((t) => (currentId === 0 ? t.is_system_default : currentId === t.id));
  const next = data.find((t) => (nextId === 0 ? t.is_system_default : nextId === t.id));

  invariant(current && next && current.id !== next.id);

  const candidates: RemapCandidate[] = [];
  const remapStates: RemapStateGroup[] = [];
  let index = 0;
  blockStates.forEach((state: BlockState) => {
    const remapGroup: RemapStateGroup = { state, statuses: [] };
    const nextStatusesForState = next.statuses[state].map((s) => ({ ...s, state }));
    current.statuses[state].forEach((status) => {
      const current = { ...status, state };
      remapGroup.statuses.push({
        index: index++,
        current,
        options: nextStatusesForState,
      });
      candidates.push({
        current,
        next: nextStatusesForState.length === 1 ? nextStatusesForState[0] : null,
      });
    });

    remapStates.push(remapGroup);
  });

  return {
    initialValues: {
      statuses: candidates,
    },
    remap: {
      current,
      next,
      states: remapStates,
    },
  };
};

type StatusRemapDialogProps = RenderProps<{ statuses: RemapCandidate[] }> & {
  currentId: number;
  nextId: number;
};

export function StatusRemapDialog({ onSubmit, currentId, nextId }: StatusRemapDialogProps): ReactElement {
  const props = useProps(currentId, nextId);

  return (
    <Form<{ statuses: RemapCandidate[] }> onSubmit={onSubmit} initialValues={props?.initialValues} autoComplete="off">
      <DialogHeader title="Template migration" />
      <DialogBody>
        <Alert variant="warning" className="mb-2">
          <Box textSize="xs">
            You are updating the Block (Media Plan) statuses for a media type. Please map the existing statuses in the
            first column to the corresponding statuses in the second column.
          </Box>
        </Alert>
        <LoadingContainer loading={!props}>{props && <RemapForm {...props.remap} />}</LoadingContainer>
      </DialogBody>
      <DialogFooter>
        <Submit>Confirm</Submit>
      </DialogFooter>
    </Form>
  );
}
